import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Store,
  Palette,
  DollarSign,
  FileText,
  Code,
  Footprints,
  Settings,
  Mail,
  Users,
  Shield,
  Key,
  Database,
  Bot,
  Bell,
  Share2,
  ChevronRight,
} from 'lucide-react';
import { useSettingsSlice } from './slice';

// Import individual setting components
import GeneralSettings from './components/GeneralSettings';

import FooterSettings from './components/FooterSettings';
import SocialLinksSettings from './components/SocialLinksSettings';
import CurrencySettings from './components/CurrencySettings';
import CustomSettings from './components/CustomSettings';
import LicenseSettings from './components/LicenseSettings';
import AdvancedSettings from './components/AdvancedSettings';
import SocialAuthSettings from './components/SocialAuthSettings';
import RedisSettings from './components/RedisSettings';
import SMTPSettings from './components/SMTPSettings';
import OpenAISettings from './components/OpenAISettings';
import JWTSettings from './components/JWTSettings';
import NotificationHeaderSettings from './components/NotificationHeaderSettings';
import MasterPasswordSettings from './components/MasterPasswordSettings';

// Sidebar menu items - each as a separate main item
const sidebarItems = [
  {
    id: 'general',
    label: 'General',
    icon: Store,
    description: 'Basic site information and configuration',
  },
  {
    id: 'footer',
    label: 'Footer',
    icon: Footprints,
    description: 'Footer content and social links',
  },
  {
    id: 'social-links',
    label: 'Social Links',
    icon: Share2,
    description: 'Social media profiles and floating buttons',
  },
  {
    id: 'currency',
    label: 'Currency',
    icon: DollarSign,
    description: 'Currency and payment settings',
  },
  {
    id: 'custom',
    label: 'Custom',
    icon: Code,
    description: 'Custom CSS, JS, and code',
  },
  {
    id: 'license',
    label: 'License',
    icon: FileText,
    description: 'License and subscription details',
  },
  {
    id: 'advanced',
    label: 'Advanced',
    icon: Settings,
    description: 'Advanced system configuration',
  },
  {
    id: 'social',
    label: 'Social Auth',
    icon: Users,
    description: 'Social authentication settings',
  },
  {
    id: 'master-password',
    label: 'Master Password',
    icon: Shield,
    description: 'Master password for administrative access',
  },
  {
    id: 'redis',
    label: 'Redis',
    icon: Database,
    description: 'Redis cache configuration',
  },
  {
    id: 'smtp',
    label: 'SMTP',
    icon: Mail,
    description: 'Email server configuration',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    icon: Bot,
    description: 'OpenAI API configuration',
  },
  {
    id: 'jwt',
    label: 'JWT',
    icon: Key,
    description: 'JWT authentication configuration',
  },
  {
    id: 'notification-header',
    label: 'Notification Header',
    icon: Bell,
    description: 'Header banner and notification settings',
  },
];

export default function SiteSettings() {
  const { useGetSiteSettingsQuery } = useSettingsSlice();
  const [getSiteSettings, { data: siteData, isLoading }] =
    useGetSiteSettingsQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get active tab from URL params or localStorage, default to 'general'
  const getInitialActiveTab = () => {
    const urlTab = searchParams.get('tab');
    if (urlTab && sidebarItems.some(item => item.id === urlTab)) {
      return urlTab;
    }

    const storedTab = localStorage.getItem('siteSettingsActiveTab');
    if (storedTab && sidebarItems.some(item => item.id === storedTab)) {
      return storedTab;
    }

    return 'general';
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  // Update URL and localStorage when active tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);

    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabId);
    setSearchParams(newSearchParams);

    // Update localStorage
    localStorage.setItem('siteSettingsActiveTab', tabId);
  };

  // Watch for URL parameter changes and update active tab
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && sidebarItems.some(item => item.id === urlTab)) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);

  useEffect(() => {
    getSiteSettings({});
  }, []);

  // set the default values to the data
  useEffect(() => {
    if (siteData) {
      const settings = siteData.data;
      console.log('Raw API settings:', settings);
      console.log('Email verification from API:', {
        raw: settings.email_verification_enabled,
        type: typeof settings.email_verification_enabled,
        boolean: Boolean(settings.email_verification_enabled),
      });

      // Transform integer boolean values (0/1) to actual booleans and provide defaults
      const transformedSettings = {
        // General Settings
        site_name: settings.site_name || '',
        store_name: settings.store_name || '',
        store_tagline: settings.store_tagline || '',
        store_phone: settings.store_phone || '',
        store_email: settings.store_email || '',
        store_address: settings.store_address || '',
        store_city: settings.store_city || '',
        store_state: settings.store_state || '',
        store_zip: settings.store_zip || '',
        store_country: settings.store_country || '',
        store_timezone: settings.store_timezone || '',
        store_currency: settings.store_currency || '',
        store_language: settings.store_language || '',
        store_description: settings.store_description || '',
        store_keywords: settings.store_keywords || '',
        store_author: settings.store_author || '',
        store_copyright: settings.store_copyright || '',
        store_logo: settings.store_logo || '',
        store_favicon: settings.store_favicon || '',

        // Branding Settings
        logo: settings.logo || '',
        logo_dark: settings.logo_dark || '',
        logo_width: settings.logo_width || 200,
        logo_height: settings.logo_height || 60,
        favicon: settings.favicon || '',
        text_logo: settings.text_logo || '',
        folder_icon: settings.folder_icon || '',
        file_icon: settings.file_icon || '',

        // Color Settings
        primary_color: settings.primary_color || '',
        primary_color_dark: settings.primary_color_dark || '',
        secondary_color: settings.secondary_color || '',
        secondary_color_dark: settings.secondary_color_dark || '',
        accent_color: settings.accent_color || '',
        accent_color_dark: settings.accent_color_dark || '',
        background_color: settings.background_color || '',
        background_color_dark: settings.background_color_dark || '',
        text_color: settings.text_color || '',
        text_color_dark: settings.text_color_dark || '',

        // Typography Settings
        font_family: settings.font_family || 'Inter',
        font_size: settings.font_size || '16px',
        theme_mode: settings.theme_mode || 'light',

        // Header Banner Settings
        header_banner_enabled: Boolean(settings.header_banner_enabled),
        header_banner_style: settings.header_banner_style || 'gradient',
        header_banner_show_recent: Boolean(settings.header_banner_show_recent),
        header_banner_show_top: Boolean(settings.header_banner_show_top),

        // Header Navigation Settings
        header_nav_enabled: Boolean(settings.header_nav_enabled),
        header_nav_style: settings.header_nav_style || 'dark',
        header_nav_bg_color: settings.header_nav_bg_color || 'bg-gray-900',
        header_nav_text_color: settings.header_nav_text_color || 'text-white',

        // Footer Settings
        footer_enabled: Boolean(settings.footer_enabled),
        footer_text: settings.footer_text || '',
        footer_logo: settings.footer_logo || '',
        footer_background_color: settings.footer_background_color || '',
        footer_text_color: settings.footer_text_color || '',

        // Custom Settings
        custom_css_enabled: Boolean(settings.custom_css_enabled),
        custom_css: settings.custom_css || '',
        custom_js_enabled: Boolean(settings.custom_js_enabled),
        custom_js: settings.custom_js || '',

        // License Settings
        license_key: settings.license_key || '',
        license_status: settings.license_status || '',
        license_expiry: settings.license_expiry || '',

        // Advanced Settings
        maintenance_mode: Boolean(settings.maintenance_mode),
        debug_mode: Boolean(settings.debug_mode),
        cache_enabled: Boolean(settings.cache_enabled),
        seo_enabled: Boolean(settings.seo_enabled),

        // Social Auth Settings
        social_auth_enabled: Boolean(settings.social_auth_enabled),
        google_auth_enabled: Boolean(settings.google_auth_enabled),
        facebook_auth_enabled: Boolean(settings.facebook_auth_enabled),
        twitter_auth_enabled: Boolean(settings.twitter_auth_enabled),

        // Redis Settings
        redis_enabled: Boolean(settings.redis_enabled),
        redis_host: settings.redis_host || 'localhost',
        redis_port: settings.redis_port || 6379,
        redis_password: settings.redis_password || '',
        redis_database: settings.redis_database || 0,

        // SMTP Settings
        smtp_enabled: Boolean(settings.smtp_enabled),
        smtp_host: settings.smtp_host || '',
        smtp_port: settings.smtp_port || 587,
        smtp_username: settings.smtp_username || '',
        smtp_password: settings.smtp_password || '',
        smtp_encryption: settings.smtp_encryption || 'tls',

        // OpenAI Settings
        openai_enabled: Boolean(settings.openai_enabled),
        openai_api_key: settings.openai_api_key || '',
        openai_model: settings.openai_model || 'gpt-3.5-turbo',
        openai_max_tokens: settings.openai_max_tokens || 1000,

        // JWT Settings
        jwt_enabled: Boolean(settings.jwt_enabled),
        jwt_secret: settings.jwt_secret || '',
        jwt_expiry: settings.jwt_expiry || '24h',

        // Notification Header Settings
        notification_header_enabled: Boolean(
          settings.notification_header_enabled,
        ),
        notification_header_title: settings.notification_header_title || '',
        notification_header_message: settings.notification_header_message || '',
        notification_header_link_text:
          settings.notification_header_link_text || '',
        notification_header_link_url:
          settings.notification_header_link_url || '',
        notification_header_background_color:
          settings.notification_header_background_color || '',
        notification_header_text_color:
          settings.notification_header_text_color || '',
        notification_header_link_color:
          settings.notification_header_link_color || '',
        notification_header_dismissible: Boolean(
          settings.notification_header_dismissible,
        ),
        notification_header_show_on_mobile: Boolean(
          settings.notification_header_show_mobile,
        ),
        notification_header_start_date:
          settings.notification_header_start_date || '',
        notification_header_end_date:
          settings.notification_header_end_date || '',
        notification_header_show_countdown: Boolean(
          settings.notification_header_show_countdown,
        ),
        notification_header_icon: settings.notification_header_icon || '',
        notification_header_animation:
          settings.notification_header_animation || '',

        // Master Password Settings
        master_password_enabled: Boolean(settings.master_password_enabled),
        master_password: settings.master_password || '',
        master_password_confirm: settings.master_password_confirm || '',

        // Verification & Security Settings
        email_verification_enabled:
          settings.email_verification_enabled === '1' ||
          settings.email_verification_enabled === 1 ||
          settings.email_verification_enabled === true,
        mobile_verification_enabled:
          settings.mobile_verification_enabled === '1' ||
          settings.mobile_verification_enabled === 1 ||
          settings.mobile_verification_enabled === true,
        download_expiry_days: settings.download_expiry_days || 1,
        default_country_code: settings.default_country_code || '',
      };

      form.reset(transformedSettings);
    }
  }, [siteData]);

  const form = useForm({
    defaultValues: {},
  });

  // Render the appropriate component based on active tab
  const renderActiveComponent = () => {
    const props = { form };

    switch (activeTab) {
      case 'general':
        return <GeneralSettings {...props} />;
      case 'footer':
        return <FooterSettings {...props} />;
      case 'social-links':
        return <SocialLinksSettings />;
      case 'currency':
        return <CurrencySettings />;
      case 'custom':
        return <CustomSettings {...props} />;
      case 'license':
        return <LicenseSettings {...props} />;
      case 'advanced':
        return <AdvancedSettings {...props} />;
      case 'social':
        return <SocialAuthSettings {...props} />;
      case 'master-password':
        return <MasterPasswordSettings {...props} />;
      case 'redis':
        return <RedisSettings {...props} />;
      case 'smtp':
        return <SMTPSettings {...props} />;
      case 'openai':
        return <OpenAISettings {...props} />;
      case 'jwt':
        return <JWTSettings {...props} />;
      case 'notification-header':
        return <NotificationHeaderSettings {...props} />;
      default:
        return <GeneralSettings {...props} />;
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Site Settings
          </h2>

          <nav className="space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary border-r-2 border-primary'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {item.description}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                {sidebarItems.find(item => item.id === activeTab)?.label}
              </h1>
              <p className="text-gray-600 mt-1">
                {sidebarItems.find(item => item.id === activeTab)?.description}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">{renderActiveComponent()}</div>
        </div>
      </div>
    </div>
  );
}
